import React from 'react'
import { motion, useScroll, useSpring } from 'framer-motion'

// Tone styles for list items
const TONES = {
  good: { item: 'bg-emerald-50 ring-emerald-200 hover:ring-emerald-400', dot: 'bg-emerald-500', icon: 'M5 13l4 4L19 7' },
  bad: { item: 'bg-rose-50 ring-rose-200 hover:ring-rose-400', dot: 'bg-rose-500', icon: 'M6 18L18 6M6 6l12 12' },
  neutral: { item: 'bg-white ring-slate-200 hover:ring-indigo-400', dot: 'bg-indigo-500', icon: 'M5 13l4 4L19 7' },
}

// Section accent colors (cycled)
const ACCENTS = [
  'from-indigo-500 to-sky-400',
  'from-violet-500 to-fuchsia-400',
  'from-sky-500 to-cyan-400',
  'from-emerald-500 to-teal-400',
  'from-amber-500 to-orange-400',
  'from-rose-500 to-pink-400',
]

const SECTIONS = [
  {
    title: '14-Day Refund Period',
    blocks: [
      { p: 'You may request a refund within 14 working days from the date your payment was made.' },
      { p: 'Refund requests submitted after the 14-days period may not be eligible for a refund, except where required by applicable law or at the sole discretion of Vector Visionary.' },
    ],
  },
  {
    title: 'Eligibility for a Refund',
    blocks: [
      { p: 'A refund may be considered if:' },
      {
        tone: 'good',
        ul: [
          'Vector Visionary has not yet started working on your order.',
          'We are unable to provide the service you purchased.',
          'The delivered work substantially fails to match the agreed project requirements and we are unable to reasonably correct the issue.',
          'You have been charged incorrectly or more than once for the same service.',
          'There is another legitimate issue that warrants a refund, as determined by Vector Visionary.',
        ],
      },
    ],
  },
  {
    title: 'Custom and Digital Services',
    blocks: [
      { p: 'Most services provided by Vector Visionary are custom-made digital services. This includes, but is not limited to:' },
      {
        tone: 'neutral',
        grid: true,
        ul: [
          'Embroidery digitizing',
          'Vector artwork conversion',
          'Logo/vector recreation',
          'Graphic design',
          'Print-ready artwork',
          'Embroidery files',
          'DST and other embroidery file formats',
          'Digital artwork and design modifications',
        ],
      },
      { p: 'Because these services involve time, expertise, and customized work, a refund may not be available once substantial work has already been completed or final files have been delivered.' },
      { p: 'If you are not satisfied with the result, we encourage you to contact us first. Where reasonably possible, we will work with you to make revisions or corrections according to the original project requirements.' },
    ],
  },
  {
    title: 'Orders Already in Progress',
    blocks: [
      { p: 'If work has already started on your project, any refund request will be reviewed based on the amount of work completed.' },
      { p: 'Depending on the circumstances, Vector Visionary may:' },
      {
        tone: 'neutral',
        ul: [
          'Provide revisions or corrections;',
          'Offer a partial refund;',
          'Provide an alternative solution; or',
          'Decline the refund request if the majority of the agreed work has already been completed.',
        ],
      },
    ],
  },
  {
    title: 'Completed Orders and Delivered Files',
    blocks: [
      { p: 'Once the final files have been completed and delivered, refunds are generally not available simply because you have changed your mind, no longer require the design, or do not like the design style when the work was completed according to the agreed requirements.' },
      { p: 'However, if there is a significant technical or quality issue, please contact us so that we can review the matter and determine an appropriate solution.' },
    ],
  },
  {
    title: 'Revisions',
    blocks: [
      { p: 'If your project includes revisions, we encourage you to use the included revision opportunities before requesting a refund.' },
      { p: 'Revision requests should remain reasonably consistent with the original project brief. Requests for a completely new concept, substantially different artwork, or additional services may be treated as a new project and may incur additional charges.' },
    ],
  },
  {
    title: 'Non-Refundable Situations',
    blocks: [
      { p: 'A refund generally will not be provided when:' },
      {
        tone: 'bad',
        grid: true,
        ul: [
          'The client changes their mind after work has started.',
          'The client provides incorrect, incomplete, or low-quality source material.',
          'The client does not provide required information or feedback in a timely manner.',
          'The project has been completed according to the agreed specifications.',
          'Final files have already been delivered and there is no material defect.',
          'The client requests changes outside the original project scope.',
          'The client has approved the final design or artwork.',
          'The client fails to respond to our communication for an extended period.',
          'The refund request is made after the 14-day refund period.',
        ],
      },
    ],
  },
  {
    title: 'Payment Processing Fees',
    blocks: [
      { p: "Where a refund is approved, Vector Visionary may deduct non-refundable third-party payment processing fees where permitted by applicable law and the payment provider's terms." },
      { p: 'The exact refund amount will be communicated to the client before processing where applicable.' },
    ],
  },
  {
    title: 'How to Request a Refund',
    blocks: [
      { p: 'To request a refund, please contact us through our official contact channels and provide:' },
      {
        tone: 'neutral',
        grid: true,
        ul: [
          'Your full name',
          'Order or invoice number',
          'Date of purchase',
          'Service purchased',
          'Reason for the refund request',
          'Any relevant files, screenshots, or supporting information',
        ],
      },
      { p: 'Please submit your request within 14 calendar days of payment.' },
      { p: 'We may request additional information to properly review your request.' },
    ],
  },
  {
    title: 'Refund Review Process',
    blocks: [
      { p: 'After receiving your request, Vector Visionary will review the project, payment, communications, and work completed.' },
      { p: 'We aim to respond to refund requests within a reasonable timeframe.' },
      { p: 'If a refund is approved, it will generally be issued through the original payment method, where technically possible.' },
      { p: 'Processing times may vary depending on the payment provider, bank, or financial institution.' },
    ],
  },
  {
    title: 'Chargebacks and Payment Disputes',
    blocks: [
      { p: 'Before initiating a chargeback or payment dispute with your bank or payment provider, we strongly encourage you to contact Vector Visionary so we can attempt to resolve the issue directly.' },
      { p: 'Unauthorized or fraudulent chargebacks may result in the suspension of current or future services while the matter is investigated.' },
    ],
  },
  {
    title: 'Service Quality Concerns',
    blocks: [
      { p: 'If you believe your order contains an error, technical issue, or does not meet the agreed specifications, please contact us as soon as possible.' },
      { p: 'For embroidery digitizing, for example, please provide details regarding the issue and, where applicable, photographs or videos of the stitched sample. This allows us to properly assess the problem and determine whether corrections or revisions are appropriate.' },
    ],
  },
  {
    title: 'Policy Changes',
    blocks: [
      { p: 'Vector Visionary reserves the right to update or modify this Refund Policy at any time. Any changes will be posted on this page with an updated effective date.' },
      { p: 'The policy applicable to your purchase will generally be the policy in effect at the time of your payment, subject to applicable law.' },
    ],
  },
  {
    title: 'Contact Us',
    blocks: [
      { p: 'If you have questions regarding this Refund Policy or would like to request a refund, please contact Vector Visionary through our official website.' },
      { contact: true },
    ],
  },
]

const Bullet = ({ tone, children }) => {
  const t = TONES[tone]
  return (
    <li className={`group flex items-start gap-3 rounded-2xl p-4 ring-1 text-slate-700 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${t.item}`}>
      <span className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-white ${t.dot}`}>
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d={t.icon} />
        </svg>
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  )
}

const Contact = () => (
  <div className="relative overflow-hidden rounded-3xl bg-slate-900 p-8 text-white shadow-2xl">
    <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-indigo-500/40 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-sky-400/30 blur-3xl" />
    <div className="relative space-y-1">
      <p className="mb-3 bg-gradient-to-r from-indigo-300 to-sky-300 bg-clip-text text-2xl font-extrabold text-transparent">Vector Visionary</p>
      <p className="text-slate-300">Website: vectorvisionary.com</p>
      <p className="text-slate-300">Email: accounts@vectorvisionary.com or team@vectorvisionary.com</p>
      <p className="text-slate-300">Phone: (209) 964-6220</p>
    </div>
  </div>
)

const RefundPage = () => {
  const { scrollYProgress } = useScroll()
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 })

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Scroll progress */}
      <motion.div style={{ scaleX: progress }} className="fixed left-0 top-0 z-50 h-1 w-full origin-left bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-sky-400" />

      {/* Hero */}
      <header className="relative overflow-hidden bg-slate-950 px-4 pb-32 pt-24 text-center">
        <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-indigo-600/40 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-sky-500/30 blur-3xl" />
        <div className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative mx-auto max-w-3xl">
          <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-indigo-500 to-sky-400 shadow-2xl shadow-indigo-500/40 ring-4 ring-white/10">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
          </div>
          <h1 className="bg-gradient-to-r from-white via-indigo-200 to-sky-300 bg-clip-text text-5xl font-black tracking-tight text-transparent md:text-7xl">
            14-Day Refund Policy
          </h1>
          <p className="mt-6 inline-block rounded-full bg-white/10 px-6 py-2 text-lg font-medium text-slate-200 ring-1 ring-white/20 backdrop-blur">
            Effective Date: August 31, 2026
          </p>
        </motion.div>
      </header>

      <div className="relative mx-auto -mt-20 max-w-4xl px-4 pb-24">
        {/* Intro card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="rounded-3xl bg-white p-8 shadow-2xl shadow-indigo-900/10 ring-1 ring-slate-900/5 md:p-12"
        >
          <p className="mb-6 text-xl font-medium leading-relaxed text-slate-800">
            At Vector Visionary, we are committed to providing high-quality embroidery digitizing, vector artwork conversion, graphic design, and other digital design services. We want every client to be satisfied with our work.
          </p>
          <p className="text-lg leading-relaxed text-slate-600">
            This Refund Policy explains the circumstances under which you may request a refund for services purchased from Vector Visionary.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="mt-10 space-y-8">
          {SECTIONS.map((s, i) => (
            <section key={s.title} className="rounded-3xl bg-white p-8 shadow-lg ring-1 ring-slate-900/5 md:p-10">
              <div className="mb-6 flex items-center gap-4">
                <span className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${ACCENTS[i % ACCENTS.length]} text-xl font-black text-white shadow-lg`}>
                  {i + 1}.
                </span>
                <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">{s.title}</h2>
              </div>
              <div className={`mb-6 h-1 w-full rounded-full bg-gradient-to-r ${ACCENTS[i % ACCENTS.length]} opacity-20`} />
              <div className="space-y-5">
                {s.blocks.map((b, j) => {
                  if (b.p) return <p key={j} className="text-[1.05rem] leading-relaxed text-slate-600">{b.p}</p>
                  if (b.contact) return <Contact key={j} />
                  return (
                    <ul key={j} className={`grid list-none gap-3 pl-0 ${b.grid ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                      {b.ul.map((t) => <Bullet key={t} tone={b.tone}>{t}</Bullet>)}
                    </ul>
                  )
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Closing */}
        <div className="mt-10 rounded-3xl bg-gradient-to-r from-indigo-600 via-violet-600 to-sky-500 p-8 text-center text-lg font-medium leading-relaxed text-white shadow-xl">
          We appreciate your business and are committed to providing professional, reliable, and high-quality digital design services.
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8 text-center text-sm text-slate-400">
          <p>This document was last updated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  )
}

export default RefundPage